import { createContext, useContext, useReducer, useRef, useCallback, useEffect, type ReactNode } from 'react'
import { parseEvent, type DetectionNotification, type NotificationVariant } from './shared'
import { PeekCards } from './PeekCards'
import { NotificationDrawer } from './NotificationDrawer'

// ── Constants ───────────────────────────────────────────────────────────────

const MAX_PEEK = 4
const MAX_HISTORY = 50
const ALERT_DURATION = 8_000
const STANDARD_DURATION = 6_000
const HOVER_RESUME_DELAY = 3_000

// ── Context ─────────────────────────────────────────────────────────────────

interface NotificationContextValue {
  notifications: DetectionNotification[]
  peekIds: string[]
  exitingPeekIds: string[]
  unreadCount: number
  drawerOpen: boolean
  activeTab: 'all' | 'alerts' | 'motion'
  addNotification: (event: any) => void
  dismissPeek: (id: string) => void
  dismissFromDrawer: (id: string) => void
  toggleDrawer: () => void
  markAllRead: () => void
  clearAll: () => void
  setActiveTab: (tab: 'all' | 'alerts' | 'motion') => void
  pausePeekTimer: (id: string) => void
  resumePeekTimer: (id: string) => void
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

export function useNotifications() {
  const ctx = useContext(NotificationContext)
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider')
  return ctx
}

// ── Reducer ─────────────────────────────────────────────────────────────────

interface State {
  notifications: DetectionNotification[]
  peekIds: string[]
  exitingPeekIds: string[]
  unreadCount: number
  drawerOpen: boolean
  activeTab: 'all' | 'alerts' | 'motion'
}

type Action =
  | { type: 'ADD'; notification: DetectionNotification }
  | { type: 'EXIT_PEEK'; id: string }
  | { type: 'DISMISS_PEEK'; id: string }
  | { type: 'DISMISS_FROM_DRAWER'; id: string }
  | { type: 'TOGGLE_DRAWER' }
  | { type: 'MARK_ALL_READ' }
  | { type: 'CLEAR_ALL' }
  | { type: 'SET_TAB'; tab: 'all' | 'alerts' | 'motion' }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'ADD': {
      const notifications = [action.notification, ...state.notifications].slice(0, MAX_HISTORY)
      let peekIds = [action.notification.id, ...state.peekIds]
      if (peekIds.length > MAX_PEEK) peekIds = peekIds.slice(0, MAX_PEEK)
      return { ...state, notifications, peekIds, unreadCount: state.unreadCount + 1 }
    }
    case 'EXIT_PEEK':
      return {
        ...state,
        exitingPeekIds: state.exitingPeekIds.includes(action.id)
          ? state.exitingPeekIds
          : [...state.exitingPeekIds, action.id],
      }
    case 'DISMISS_PEEK':
      return {
        ...state,
        peekIds: state.peekIds.filter(id => id !== action.id),
        exitingPeekIds: state.exitingPeekIds.filter(id => id !== action.id),
      }
    case 'DISMISS_FROM_DRAWER': {
      const peekIds = state.peekIds.filter(id => id !== action.id)
      const exitingPeekIds = state.exitingPeekIds.filter(id => id !== action.id)
      const notifications = state.notifications.filter(n => n.id !== action.id)
      const removedWasUnread = state.notifications.find(n => n.id === action.id && !n.isRead)
      return {
        ...state,
        notifications,
        peekIds,
        exitingPeekIds,
        unreadCount: removedWasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      }
    }
    case 'TOGGLE_DRAWER': {
      const opening = !state.drawerOpen
      if (opening) {
        return {
          ...state,
          drawerOpen: true,
          unreadCount: 0,
          notifications: state.notifications.map(n => ({ ...n, isRead: true })),
        }
      }
      return { ...state, drawerOpen: false }
    }
    case 'MARK_ALL_READ':
      return {
        ...state,
        unreadCount: 0,
        notifications: state.notifications.map(n => ({ ...n, isRead: true })),
      }
    case 'CLEAR_ALL':
      return { ...state, notifications: [], peekIds: [], exitingPeekIds: [], unreadCount: 0 }
    case 'SET_TAB':
      return { ...state, activeTab: action.tab }
    default:
      return state
  }
}

// ── Provider ────────────────────────────────────────────────────────────────

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {
    notifications: [],
    peekIds: [],
    exitingPeekIds: [],
    unreadCount: 0,
    drawerOpen: false,
    activeTab: 'all',
  })

  // ── Timer management ──
  // Map<id, { timeout, remaining, startedAt }>
  const timersRef = useRef<Map<string, { timeout: ReturnType<typeof setTimeout>; remaining: number; startedAt: number }>>(new Map())

  const dismissPeek = useCallback((id: string) => {
    const t = timersRef.current.get(id)
    if (t) { clearTimeout(t.timeout); timersRef.current.delete(id) }
    dispatch({ type: 'DISMISS_PEEK', id })
  }, [])

  const startTimer = useCallback((id: string, duration: number) => {
    const existing = timersRef.current.get(id)
    if (existing) clearTimeout(existing.timeout)
    const timeout = setTimeout(() => {
      timersRef.current.delete(id)
      // Signal exit animation — PeekCard will call dismissPeek after animation completes
      dispatch({ type: 'EXIT_PEEK', id })
    }, duration)
    timersRef.current.set(id, { timeout, remaining: duration, startedAt: Date.now() })
  }, [])

  const pausePeekTimer = useCallback((id: string) => {
    const t = timersRef.current.get(id)
    if (!t) return
    clearTimeout(t.timeout)
    const elapsed = Date.now() - t.startedAt
    const remaining = Math.max(0, t.remaining - elapsed)
    timersRef.current.set(id, { timeout: t.timeout, remaining, startedAt: t.startedAt })
  }, [])

  const resumePeekTimer = useCallback((id: string) => {
    const t = timersRef.current.get(id)
    if (!t) return
    const duration = Math.max(HOVER_RESUME_DELAY, t.remaining)
    startTimer(id, duration)
  }, [startTimer])

  const addNotification = useCallback((event: any) => {
    const parsed = parseEvent(event)
    const detectedObjects = parsed.detectedObjects
    const variant: NotificationVariant = detectedObjects.length > 0 ? 'alert' : 'standard'
    const id = crypto.randomUUID()
    const notification: DetectionNotification = {
      ...parsed,
      id,
      variant,
      isRead: false,
      createdAt: Date.now(),
    }
    dispatch({ type: 'ADD', notification })
    const duration = variant === 'alert' ? ALERT_DURATION : STANDARD_DURATION
    startTimer(id, duration)
  }, [startTimer])

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(t => clearTimeout(t.timeout))
      timersRef.current.clear()
    }
  }, [])

  const toggleDrawer = useCallback(() => dispatch({ type: 'TOGGLE_DRAWER' }), [])
  const markAllRead = useCallback(() => dispatch({ type: 'MARK_ALL_READ' }), [])
  const clearAll = useCallback(() => {
    timersRef.current.forEach(t => clearTimeout(t.timeout))
    timersRef.current.clear()
    dispatch({ type: 'CLEAR_ALL' })
  }, [])
  const dismissFromDrawer = useCallback((id: string) => {
    const t = timersRef.current.get(id)
    if (t) { clearTimeout(t.timeout); timersRef.current.delete(id) }
    dispatch({ type: 'DISMISS_FROM_DRAWER', id })
  }, [])
  const setActiveTab = useCallback((tab: 'all' | 'alerts' | 'motion') => dispatch({ type: 'SET_TAB', tab }), [])

  const value: NotificationContextValue = {
    ...state,
    addNotification,
    dismissPeek,
    dismissFromDrawer,
    toggleDrawer,
    markAllRead,
    clearAll,
    setActiveTab,
    pausePeekTimer,
    resumePeekTimer,
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <PeekCards />
      <NotificationDrawer />
    </NotificationContext.Provider>
  )
}
